<template>
  <div class="order-detail-page">
    <van-nav-bar title="订单详情" left-arrow @click-left="goBack" />

    <van-loading v-if="loading" class="page-loading" size="24px">加载中...</van-loading>

    <template v-else-if="order">
      <div class="detail-card">
        <div class="detail-header">
          <span class="order-no">{{ order.order_no }}</span>
          <span :class="['order-status', 'status-' + order.status]">{{ order.status_text }}</span>
        </div>
        <div v-if="order.refund_status_text" class="refund-tip">{{ order.refund_status_text }}</div>

        <div class="product-row">
          <div class="product-image">
            <img
              v-if="order.cover_img"
              :src="$common.polishingImg(order.cover_img)"
              :alt="order.tit"
            />
            <div v-else class="placeholder-img">{{ order.tit ? order.tit[0] : '' }}</div>
          </div>
          <div class="product-info">
            <div class="product-name">{{ order.tit }}</div>
            <div class="product-meta">{{ order.classify }}类 · {{ order.reg_no }}</div>
            <div class="product-type">{{ order.need_notary_text }}</div>
          </div>
        </div>

        <div class="price-block">
          <div class="price-row">
            <span>订单总价</span>
            <span class="price">¥{{ formatPrice(order.selling_price) }}</span>
          </div>
          <div class="price-row">
            <span>已付金额</span>
            <span>¥{{ formatPrice(order.paid_amount) }}</span>
          </div>
          <div v-if="order.need_notary === 1" class="price-row sub">
            <span>订金 / 尾款</span>
            <span>¥{{ formatPrice(order.deposit_amount) }} / ¥{{ formatPrice(order.final_amount) }}</span>
          </div>
        </div>

        <div class="info-block">
          <div class="info-row"><span>买家</span><span>{{ order.buyer_name }}</span></div>
          <div class="info-row"><span>电话</span><span>{{ order.buyer_phone }}</span></div>
          <div v-if="order.buyer_company" class="info-row"><span>公司</span><span>{{ order.buyer_company }}</span></div>
          <div v-if="order.buyer_remark" class="info-row"><span>备注</span><span>{{ order.buyer_remark }}</span></div>
          <div class="info-row"><span>下单时间</span><span>{{ order.created_at }}</span></div>
          <div v-if="order.refund_reason" class="info-row"><span>退款原因</span><span>{{ order.refund_reason }}</span></div>
        </div>
      </div>

      <div v-if="order.logs && order.logs.length" class="log-card">
        <div class="section-title">操作记录</div>
        <div v-for="(log, index) in order.logs" :key="index" class="log-item">
          <div class="log-time">{{ log.created_at }}</div>
          <div class="log-text">{{ log.action }} · {{ log.remark || log.operator }}</div>
        </div>
      </div>

      <div class="action-bar">
        <van-button
          v-if="order.status === STATUS_PENDING"
          block
          round
          @click="cancelOrder"
        >取消订单</van-button>
        <van-button
          v-if="order.status === STATUS_PENDING"
          block
          round
          type="danger"
          :loading="paying"
          @click="goPay"
        >去支付</van-button>
        <van-button
          v-if="canApplyRefund(order)"
          block
          round
          type="warning"
          plain
          @click="applyRefund"
        >申请退款</van-button>
        <van-button
          v-if="order.status === STATUS_NOTARIZED"
          block
          round
          type="danger"
          :loading="paying"
          @click="goPayFinal"
        >支付尾款</van-button>
      </div>
    </template>

    <van-empty v-else description="订单不存在或无权查看" />

    <van-popup v-model="showQrCode" round closeable position="center" class="qr-popup" @closed="stopPayPolling">
      <div class="qr-content">
        <div class="qr-title">请使用微信或支付宝扫码支付</div>
        <canvas id="orderDetailQrCanvas" class="qr-canvas"></canvas>
        <van-button plain round block style="margin-top: 16px;" @click="checkPayStatus(true)">
          支付完成
        </van-button>
      </div>
    </van-popup>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import { canApplyRefund } from '../constants/orderRefund'

export default {
  name: 'OrderDetailPage',
  data() {
    return {
      STATUS_PENDING: 0,
      STATUS_NOTARIZED: 2,
      STATUS_CANCELLED: 5,
      loading: true,
      paying: false,
      order: null,
      showQrCode: false,
      qrCodeUrl: '',
      payTimer: null
    }
  },
  created() {
    this.loadDetail()
  },
  beforeDestroy() {
    this.stopPayPolling()
  },
  methods: {
    canApplyRefund,
    goBack() {
      this.$router.replace('/my-orders')
    },
    formatPrice(price) {
      if (!price || price <= 0) return '0'
      return Math.round(price).toLocaleString()
    },
    async loadDetail() {
      const orderId = this.$route.query.order_id
      if (!orderId) {
        this.loading = false
        return
      }

      this.loading = true
      try {
        const res = await this.$common.cusHttp(
          this,
          `/api/saas_order.php?action=order_detail&order_id=${orderId}`,
          {},
          'get'
        )
        const data = JSON.parse(res.data)
        if (data.status) {
          this.order = data.data
        } else {
          this.$toast.fail(data.desc || '加载失败')
        }
      } catch (err) {
        console.error('加载订单详情失败:', err)
        this.$toast.fail('网络错误')
      } finally {
        this.loading = false
      }
    },
    async goPay() {
      if (!this.order) return
      const type = this.order.need_notary == 1 ? 'deposit' : 'full'
      const amount = type === 'deposit' ? this.order.deposit_amount : this.order.selling_price
      await this.executePay(amount, type)
    },
    async goPayFinal() {
      if (!this.order) return
      await this.executePay(this.order.final_amount, 'final')
    },
    async executePay(amount, type) {
      this.paying = true
      try {
        const formData = new FormData()
        formData.append('order_no', this.order.order_no)
        formData.append('amount', amount)
        formData.append('type', type)

        const res = await this.$common.cusHttp(
          this,
          '/api/saas_order.php?action=pay',
          formData,
          'post'
        )
        const data = JSON.parse(res.data)
        if (!data.status) {
          this.$toast.fail(data.desc || '支付失败')
          return
        }

        const qrCodeUrl = data.data.qrCodeUrl
        const isWechat = /MicroMessenger/i.test(navigator.userAgent)
        if (isWechat) {
          window.location.href = qrCodeUrl
          return
        }

        this.qrCodeUrl = qrCodeUrl
        this.showQrCode = true
        this.$nextTick(() => {
          this.generateQrCode()
          this.startPayPolling()
        })
      } catch (err) {
        console.error('支付失败:', err)
        this.$toast.fail('网络错误')
      } finally {
        this.paying = false
      }
    },
    generateQrCode() {
      const canvas = document.getElementById('orderDetailQrCanvas')
      if (!canvas || !this.qrCodeUrl) return
      QRCode.toCanvas(canvas, this.qrCodeUrl, { width: 200, margin: 2 })
    },
    startPayPolling() {
      this.stopPayPolling()
      this.payTimer = setInterval(() => this.checkPayStatus(false), 3000)
    },
    stopPayPolling() {
      if (this.payTimer) {
        clearInterval(this.payTimer)
        this.payTimer = null
      }
    },
    async checkPayStatus(isManual) {
      if (!this.order) return
      try {
        const res = await this.$common.cusHttp(
          this,
          `/api/saas_order.php?action=check_pay_status&order_no=${this.order.order_no}`,
          {},
          'get'
        )
        const data = JSON.parse(res.data)
        if (data.status && data.data.status !== this.order.status) {
          this.stopPayPolling()
          this.showQrCode = false
          this.$toast.success('支付成功')
          await this.loadDetail()
          return
        }
        if (isManual) {
          this.$toast('暂未查询到支付结果，请稍后重试')
        }
      } catch (err) {
        console.error('查询支付状态失败:', err)
      }
    },
    cancelOrder() {
      this.$dialog.confirm({
        title: '取消订单',
        message: '确定要取消该订单吗？'
      }).then(async () => {
        const formData = new FormData()
        formData.append('order_id', this.order.id)
        formData.append('status', this.STATUS_CANCELLED)
        const res = await this.$common.cusHttp(
          this,
          '/api/saas_order.php?action=update_status',
          formData,
          'post'
        )
        const data = JSON.parse(res.data)
        if (data.status) {
          this.$toast.success('订单已取消')
          await this.loadDetail()
        } else {
          this.$toast.fail(data.desc || '取消失败')
        }
      }).catch(() => {})
    },
    applyRefund() {
      const reason = window.prompt('请填写退款原因，提交后需代卖方或平台审核')
      if (reason === null) return
      const text = reason.trim()
      if (!text) {
        this.$toast.fail('请填写退款原因')
        return
      }

      const formData = new FormData()
      formData.append('order_id', this.order.id)
      formData.append('reason', text)

      this.$common.cusHttp(this, '/api/saas_order.php?action=apply_refund', formData, 'post')
        .then(res => {
          const data = JSON.parse(res.data)
          if (data.status) {
            this.$toast.success(data.desc || '退款申请已提交')
            this.loadDetail()
          } else {
            this.$toast.fail(data.desc || '申请失败')
          }
        })
        .catch(() => this.$toast.fail('网络错误'))
    }
  }
}
</script>

<style scoped>
.order-detail-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 24px;
}

.page-loading {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

.detail-card,
.log-card {
  margin: 12px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.order-no {
  font-size: 13px;
  color: #666;
}

.order-status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
}

.order-status.status-0 { background: #fff7e6; color: #fa8c16; }
.order-status.status-4 { background: #f6ffed; color: #52c41a; }
.order-status.status-5 { background: #f5f5f5; color: #8c8c8c; }

.refund-tip {
  margin-bottom: 12px;
  font-size: 12px;
  color: #fa8c16;
  text-align: left;
}

.product-row {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.product-image,
.placeholder-img {
  width: 72px;
  height: 72px;
  border-radius: 6px;
  overflow: hidden;
  background: #f0f0f0;
  flex-shrink: 0;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.placeholder-img {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #999;
}

.product-info {
  flex: 1;
  text-align: left;
}

.product-name {
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 6px;
}

.product-meta,
.product-type {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.price-block,
.info-block {
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
}

.price-row,
.info-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.price-row .price {
  color: #f5222d;
  font-weight: 600;
}

.price-row.sub {
  font-size: 12px;
  color: #999;
}

.section-title {
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  margin-bottom: 12px;
}

.log-item {
  text-align: left;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
}

.log-item:last-child {
  border-bottom: none;
}

.log-time {
  font-size: 12px;
  color: #999;
}

.log-text {
  font-size: 13px;
  color: #333;
  margin-top: 4px;
}

.action-bar {
  padding: 0 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.qr-content {
  padding: 24px;
  text-align: center;
}

.qr-title {
  margin-bottom: 16px;
  font-size: 14px;
}

.qr-canvas {
  margin: 0 auto;
}
</style>
